import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';
import { resolveTintaPeAsteptare } from '../utils/resolve-post-auth-target';
import { pasiInscriereActive } from '../utils/pasi-inscriere';
import type { UserInfo } from '../../../providers/auth-provider.types';

/**
 * Pe paginile „cont in asteptare" (elev si profesor): reimprospateaza tokenul la 10 s si, cand
 * stadiul userului s-a schimbat, il duce unde ii e locul.
 *
 * 2026-08-28 (2.14 + 2.17): inainte verifica `isEnabled` si facea `navigate('/')`. Pentru
 * PROFESOR `isEnabled` nu se schimba la aprobare (starea lui e in `isApproved` + cele patru
 * flag-uri), deci polling-ul nu detecta niciodata aprobarea; iar `/` e redirectat la login.
 * Acum nu mai ghiceste campul: intreaba resolver-ul prin `resolveTintaPeAsteptare` — intai garda pe
 * ruta curenta, aterizarea doar dupa ce cererea nu mai e in asteptare. Aterizarea singura trimitea
 * profesorul in asteptare inapoi pe protocol si facea din «Gata» o bucla (2026-09-15).
 */
export function useAccountWaiting() {
  const { handleTokenRefresh } = useAuthProviderV2();
  const { PATHS, PAGES, ROLES, getUserHomePage } = useRoutesProvider();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Fix round 1 (2026-08-29): `handleTokenRefresh` e un `useCallback` cu `refreshToken` in deps
  // — dupa primul refresh reusit isi schimba identitatea. Cu deps `[]` mai jos, closure-ul
  // captat la montare tot trimitea refresh-token-ul VECHI la fiecare poll. Ref-urile citesc
  // mereu versiunea curenta, fara sa reporneasca intervalul.
  const refreshRef = useRef(handleTokenRefresh);
  const pathnameRef = useRef(pathname);
  // Scrisul de ref-uri se face intr-un efect, nu in corpul render-ului: router-ul e creat cu
  // `future: { v7_startTransition: true }` (main.jsx), deci randari pot fi aruncate — un render
  // renuntat ar fi apucat sa scrie totusi `pathnameRef.current`. Fara array de deps, ruleaza dupa
  // fiecare render COMMIT-uit.
  useEffect(() => {
    refreshRef.current = handleTokenRefresh;
    pathnameRef.current = pathname;
  });

  useEffect(() => {
    let cancelled = false;

    async function checkAccount() {
      try {
        const newToken = await refreshRef.current(true);
        if (!newToken || cancelled) return;
        const decoded = jwtDecode<UserInfo>(newToken);
        const target = resolveTintaPeAsteptare(decoded, pathnameRef.current, {
          PATHS,
          PAGES,
          ROLES,
          getUserHomePage,
          pasiInscriere: pasiInscriereActive(),
        });
        if (target !== null) {
          navigate(target, { replace: true });
        }
      } catch (error) {
        console.error('[useAccountWaiting] verificarea contului a esuat:', error);
      }
    }

    void checkAccount();
    const intervalId = setInterval(checkAccount, 10000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
    // Rulam o singura data la montare + interval; contextul de rute e stabil pe durata paginii.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
